import { useAjax } from '~/composables/useAjax';
import { AppCookieNames } from '../assets/constants/ecomApi';

// Utility stable stringify for consistent cache keys regardless of object key order
function stableStringify(obj: any): string {
    if (obj === null || obj === undefined) return JSON.stringify(obj);
    if (typeof obj !== 'object') return JSON.stringify(obj);
    if (Array.isArray(obj)) return `[${obj.map(stableStringify).join(',')}]`;
    const keys = Object.keys(obj).sort();
    return `{${keys.map(k => `"${k}":${stableStringify(obj[k])}`).join(',')}}`;
}

// Simple client-side cache: Map<cacheKey, { data: T, expiry: number }>
const clientCache = new Map<string, any>();

// Define composable options interface
type AjaxEcomOptions = {
    params?: object;
    method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
    query?: object;
    headers?: Record<string, string>;
    show_notifications?: boolean;
    show_loading_indicator?: boolean;
    severity?: string;
    debug?: boolean;
    cache?: boolean;
    ttl?: number;
};

export async function useAjaxEcom<T>(
    url: string,
    options: AjaxEcomOptions = {},
    nuxtApp: ReturnType<typeof useNuxtApp> | null = null,
    config: ReturnType<typeof useRuntimeConfig> | null = null,
    callback: Function | null = null
): Promise<T> {
    const rootStore = useRootStore();

    try {
        // Ensure nuxtApp and config are initialized on client
        if ((import.meta.client || !nuxtApp) && typeof useNuxtApp === 'function') {
            nuxtApp = useNuxtApp();
        }
        if ((import.meta.client || !config) && typeof useRuntimeConfig === 'function') {
            config = useRuntimeConfig();
        }

        // Compose cache key if caching enabled
        let cacheKey = '';
        if (options.cache) {
            cacheKey = url;

            if (options.query) {
                cacheKey += stableStringify(options.query);
            } else if (options.params) {
                cacheKey += stableStringify(options.params);
            }

            if (config && config.public) {
                cacheKey += `-${config.public.appEnvironment}-${config.public.version}-${config.public.appRegion}`;
            }

            const methodKey = (options.method ?? 'get').toUpperCase();
            cacheKey = `${methodKey}-${cacheKey}`;
        }

        // Check client-side cache for GET requests
        if (
            import.meta.client &&
            options.cache &&
            ((options.method ?? 'get').toLowerCase() === 'get')
        ) {
            const cached = clientCache.get(cacheKey);
            if (cached && cached.expiry > Date.now()) {
                return cached.data;
            } else if (cached) {
                // Remove expired cache
                clientCache.delete(cacheKey);
            }
        }

        // Set default flags if not provided
        if (!('show_notifications' in options)) {
            options.show_notifications = false;
        }
        if (!('debug' in options)) {
            options.debug = true;
        }
        if (!options.headers) {
            options.headers = {};
        }

        // Attach customer ID header from authStore if not already present
        const authStore = useAuthStore();
        if (!options.headers['X-Toolstation-Customer-Id']) {
            if (authStore.guest && authStore.guest.token) {
                options.headers['X-Toolstation-Customer-Id'] = authStore.guest.token;
            } else if (authStore.user && authStore.user.token) {
                options.headers['X-Toolstation-Customer-Id'] = authStore.user.token;
            }
        }

        // Attach Accept-Language header from i18n if available
        if (nuxtApp && nuxtApp.$i18n && nuxtApp.$i18n.locale?.value) {
            options.headers['Accept-Language'] = nuxtApp.$i18n.locale.value;
        }

        // Server-side additional headers for caching and User-Agent
        if (import.meta.server) {
            if (options.cache) {
                options.headers['cache'] = String(options.cache);
            }
            options.headers['cache_key'] = cacheKey;
            options.headers['cache_ttl'] = String(options.ttl ?? 90);
            options.headers['User-Agent'] = `Toolstation EU24-Web | ${config?.public.appEnvironment}-${config?.public.version}-${config?.public.appRegion}`;
        }

        // Compose base URL and full URL
        const base_url =
            import.meta.server
                ? '/api/ecom'
                : config?.public?.ecomApiBaseUrl ?? '';

        const ecom_url = base_url + url;

        // Attach Authorization header (token)
        if (
            (import.meta.server && (!config?.ecomApiBaseUrlServer)) ||
            import.meta.client
        ) {
            // Prefer rootStore token if available
            if (rootStore.access_token) {
                options.headers['Authorization'] = `Bearer ${rootStore.access_token}`;
            } else if (import.meta.server && nuxtApp?.$ecomAccessToken) {
                options.headers['Authorization'] = `Bearer ${nuxtApp.$ecomAccessToken}`;
            } else {
                const accessTokenData = useCookie<{ accessToken: string }>('ecomApiAccessToken');
                if (accessTokenData.value?.accessToken) {
                    options.headers['Authorization'] = `Bearer ${accessTokenData.value.accessToken}`;
                }
            }
        }

        // Make the main request
        const response = await useAjax<T>(ecom_url, options, callback);

        // Cache successful GET responses client-side
        if (
            import.meta.client &&
            response &&
            response.success !== false &&
            options.cache &&
            (options.method ?? 'get').toLowerCase() === 'get'
        ) {
            const ttlMs = (options.ttl ?? 90) * 1000;
            clientCache.set(cacheKey, {
                data: response,
                expiry: Date.now() + ttlMs,
            });

            // Periodic cleanup to limit cache size
            if (clientCache.size > 100) {
                const now = Date.now();
                for (const [ckey, cval] of clientCache.entries()) {
                    if (cval.expiry < now) {
                        clientCache.delete(ckey);
                    }
                }
            }
        }

        return response;
    } catch (error: any) {
        // Detect 401 token error and trigger hard reload
        const data = error?.data || error?.response?.data || {};
        const code = data?.error?.code ?? '';
        const message = data?.error?.message ?? '';

        if (
            import.meta.client &&
            error?.statusCode === 401 &&
            code.includes('401.') &&
            message === AppCookieNames.EcomApiTokenErrorMessage
        ) {
            console.warn('[useAjaxEcom] Token expired or unauthorized. Reloading page.');
            // Hard reload to refresh app state and token
            window.location.reload();
            // Return a pending promise to avoid further code execution here
            return new Promise(() => {});
        }

        console.error('[useAjaxEcom] Error during ajax call:', error?.message ?? error);

        if (import.meta.client) {
            const { $customClientErrorHandler } = useNuxtApp();
            if ($customClientErrorHandler) {
                $customClientErrorHandler(
                    error,
                    'high',
                    'Ajax Error using useAjaxEcom composable',
                    { ecomError: true }
                );
            }
        }

        // Re-throw as Nuxt error
        throw createError(error);
    }
}